import { axiosClient } from '@/shared/apis/config';
import { ApiResponse, CustomQueryOptions } from '@/shared/types';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  DtoContentStatus,
  DtoContentType,
  DtoExtension,
} from '@/shared/constants';

/**
 * v2.0 프로젝트 컨텐츠 조회 API
 * remotion-editor 에셋 메뉴에서 사용
 * ProjectContentDto 형태로 반환 (id = content.id)
 */

/**
 * Content 레벨 (표시 및 재생 용도 구분)
 * 숫자가 클수록 더 많이 노출됨. minContentLevel >= 조건으로 조회:
 * - >= DERIVED(10): 재생 필요한 모든 콘텐츠 (Editor 에셋)
 * - >= SOURCE(20): 유저 소스만 (Output 프로젝트 소스)
 *
 * - ORIGIN (0): 최초 원본 - GSR 전체 영상 (표시 안함, 재생 불필요)
 * - DERIVED (10): 파생 콘텐츠 - TTS, split 등 (Editor 에셋에만 표시, 재생 필요)
 * - SOURCE (20): 유저 입력 소스 (Output 프로젝트 소스 + Editor 에셋에 표시, 재생 필요)
 */
export const DtoContentLevel = {
  ORIGIN: 0,
  DERIVED: 10,
  SOURCE: 20,
  GENERATED: 30, // AI 생성 콘텐츠 - GTS_CHAT 영상 등
} as const;

export type DtoContentLevelType =
  (typeof DtoContentLevel)[keyof typeof DtoContentLevel];

export const ContentSourceRefScheme = z.object({
  type: z.enum(['DESKTOP_LOCAL', 'RENDER_SERVICE_NAS']),
  rootId: z.string(),
  relativePath: z.string(),
});

export type ContentSourceRef = z.infer<typeof ContentSourceRefScheme>;

export const GetProjectContentsV2RequestScheme = z.object({
  projectId: z.number(),
  contentTypeList: z.array(z.nativeEnum(DtoContentType)).optional(),
  minContentLevel: z.number(), // 필수: >= 조건 (10=DERIVED, 20=SOURCE)
});

export const ProjectContentV2Scheme = z.object({
  id: z.number(), // content.id (FolderItem.itemId가 아닌 Content.id 직접 사용)
  name: z.string(),
  contentType: z.nativeEnum(DtoContentType),
  extension: z.nativeEnum(DtoExtension),
  status: z.nativeEnum(DtoContentStatus),
  publicUrl: z.string().optional(),
  thumbnailPublicUrl: z.string().optional(),
  s3Key: z.string().optional(),
  s3Bucket: z.string().optional(),
  duration: z.number().optional(),
  fileSize: z.number().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  variants: z
    .array(
      z.object({
        type: z.string(),
        height: z.number(),
        url: z.string(),
      }),
    )
    .nullish(),
  contentLevel: z.number().optional(),
  isAsset: z.boolean().optional(),
  projectContentType: z.number().optional(), // 0=BASE, 1000=FIRST_FRAME_IMAGE 등
  generationPrompt: z.string().optional(), // 영상 생성에 사용된 프롬프트
  sourceRef: ContentSourceRefScheme.optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type ProjectContentV2 = z.infer<typeof ProjectContentV2Scheme>;
export type GetProjectContentsV2Request = z.infer<
  typeof GetProjectContentsV2RequestScheme
>;

type GetProjectContentsV2Response = ApiResponse<ProjectContentV2[]>;

export const getProjectContentsV2Api = async (
  params: GetProjectContentsV2Request,
) => {
  try {
    const response = await axiosClient.get<GetProjectContentsV2Response>(
      `/v2.0/project/${params.projectId}/contents`,
      {
        params: {
          contentType: params.contentTypeList,
          minContentLevel: params.minContentLevel,
        },
      },
    );

    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const QueryKeyUseGetProjectContentsV2Api =
  'GetProjectContentsV2Api' as const;

export const useGetProjectContentsV2Api = (
  params: GetProjectContentsV2Request,
  options?: CustomQueryOptions<GetProjectContentsV2Response>,
) => {
  const { queryKey, ...rest } = options ?? {};

  return useQuery({
    queryKey: [QueryKeyUseGetProjectContentsV2Api, params, ...(queryKey ?? [])],
    queryFn: async () => await getProjectContentsV2Api(params),
    ...rest,
  });
};
